import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import { Zap, Trophy, TrendingUp, Activity, Award } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

const PowerCurveWidget = ({ activityId, weightKg = 70 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unitMode, setUnitMode] = useState('watts'); // 'watts' o 'wkg'

  useEffect(() => {
    if (!activityId) return;
    let isMounted = true;
    setLoading(true);

    const token = localStorage.getItem('token');
    fetch(`${API_URL}/activities/${activityId}/power-curve`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(resData => {
        if (isMounted) {
          setData(resData);
          setLoading(false);
        }
      })
      .catch(err => {
        console.warn('[PowerCurveWidget] Error cargando curva de potencia:', err.message);
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [activityId]);

  if (loading) {
    return (
      <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.015)', border: '1px solid var(--glass-border)', borderRadius: '12px', textAlign: 'center' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Calculando Curva de Potencia y Récords...</span>
      </div>
    );
  }

  if (!data || (!data.activityCurve && !data.seasonBest)) {
    const isCyclingFallback = true;
    return (
      <div style={{
        background: 'rgba(255,255,255,0.015)',
        border: '1px dashed var(--glass-border)',
        borderRadius: '12px',
        padding: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{ padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
          <Zap size={20} style={{ color: 'var(--text-muted)' }} />
        </div>
        <div>
          <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Curva de Potencia / Ritmo Crítico no disponible
          </span>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>
            Esta sesión no dispone de telemetría continua (stream por segundo) para calcular los picos máximos.
          </p>
        </div>
      </div>
    );
  }

  const isCycling = data.isCycling !== false;
  const actCurve = data.activityCurve || {};
  const seasonCurve = data.seasonBest || {};

  // Formatear segundos de carrera (seg/km) a "m:ss"
  const formatPace = (secKm) => {
    if (!secKm || secKm >= 999999) return '--';
    const m = Math.floor(secKm / 60);
    const s = Math.round(secKm % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}/km`;
  };

  const durations = isCycling
    ? ['5s', '15s', '30s', '1m', '5m', '20m', '60m']
    : ['1m', '3m', '5m', '10m', '20m', '60m'];

  const chartData = durations.map(dur => {
    const actVal = actCurve[dur] || null;
    const seasonVal = seasonCurve[dur] || null;

    if (isCycling) {
      const actWatts = actVal;
      const seasonWatts = seasonVal;
      return {
        duration: dur,
        act: unitMode === 'wkg' && actWatts ? parseFloat((actWatts / weightKg).toFixed(2)) : actWatts,
        season: unitMode === 'wkg' && seasonWatts ? parseFloat((seasonWatts / weightKg).toFixed(2)) : seasonWatts
      };
    } else {
      return {
        duration: dur,
        act: actVal,
        season: seasonVal,
        actFormatted: formatPace(actVal),
        seasonFormatted: formatPace(seasonVal)
      };
    }
  }).filter(d => d.act !== null || d.season !== null);

  if (chartData.length === 0) return null;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.015)', border: '1px solid var(--glass-border)',
      borderRadius: '12px', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem'
    }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h4 style={{ fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', color: 'white', margin: 0 }}>
            <Zap size={16} style={{ color: '#f59e0b' }} />
            {isCycling ? 'Curva de Potencia Máxima (MMP)' : 'Curva de Ritmo Crítico'}
          </h4>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Comparativa: Sesión actual vs. Récord de Temporada (Últimos 90 días)
          </span>
        </div>

        {/* SELECTOR WATTS / W/KG (Solo Ciclismo) con Touch Targets ergonómicos */}
        {isCycling && (
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '3px', gap: '2px' }}>
            <button
              onClick={() => setUnitMode('watts')}
              style={{
                background: unitMode === 'watts' ? 'var(--strava-orange)' : 'transparent',
                color: unitMode === 'watts' ? 'white' : 'rgba(255,255,255,0.7)',
                border: 'none', borderRadius: '8px', minHeight: '38px', padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600,
                transition: 'all 0.15s ease'
              }}
            >
              Vatios (W)
            </button>
            <button
              onClick={() => setUnitMode('wkg')}
              style={{
                background: unitMode === 'wkg' ? 'var(--strava-orange)' : 'transparent',
                color: unitMode === 'wkg' ? 'white' : 'rgba(255,255,255,0.7)',
                border: 'none', borderRadius: '8px', minHeight: '38px', padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600,
                transition: 'all 0.15s ease'
              }}
            >
              W/kg
            </button>
          </div>
        )}
      </div>

      {/* BADGES DE PR (RÉCORDS PERSONALES DE TEMPORADA) */}
      {data.prs && data.prs.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {data.prs.map((pr, i) => (
            <div
              key={i}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.4)',
                borderRadius: '8px', padding: '0.35rem 0.75rem', fontSize: '0.78rem', color: '#fcd34d', fontWeight: 600
              }}
            >
              <Trophy size={13} style={{ color: '#f59e0b' }} />
              <span>¡Récord de Temporada en {pr.duration}: <strong>{pr.value} {pr.unit}</strong>!</span>
            </div>
          ))}
        </div>
      )}

      {/* GRÁFICA DE CURVA */}
      <div style={{ width: '100%', height: '190px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="duration" stroke="var(--text-secondary)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis
              stroke="var(--text-secondary)"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              domain={['auto', 'auto']}
              reversed={!isCycling}
              tickFormatter={!isCycling ? (val) => formatPace(val) : undefined}
            />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid var(--glass-border)', borderRadius: '8px', fontSize: '0.8rem' }}
              formatter={(val, name, item) => {
                if (!isCycling) {
                  return [formatPace(val), name === 'act' ? 'Sesión Actual' : 'Récord Temporada (90d)'];
                }
                const unit = unitMode === 'wkg' ? 'W/kg' : 'W';
                return [`${val} ${unit}`, name === 'act' ? 'Sesión Actual' : 'Récord Temporada (90d)'];
              }}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              wrapperStyle={{ fontSize: '0.75rem', paddingBottom: '6px' }}
              formatter={(val) => val === 'act' ? 'Esta Sesión' : 'Récord Temporada'}
            />
            <Line
              type="monotone"
              dataKey="season"
              stroke="rgba(255,255,255,0.35)"
              strokeDasharray="4 4"
              strokeWidth={2}
              dot={{ r: 3, fill: 'rgba(255,255,255,0.5)' }}
            />
            <Line
              type="monotone"
              dataKey="act"
              stroke={isCycling ? '#f59e0b' : '#38bdf8'}
              strokeWidth={2.5}
              dot={{ r: 4, fill: isCycling ? '#f59e0b' : '#38bdf8' }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PowerCurveWidget;
